import React, { useState } from "react";
import { ShieldCheck, Loader2, Smartphone } from "lucide-react";
import { inr } from "../lib/format";
import { GlassCard, CardHeader, StepRail, Cta, AmountChips, AmountField } from "./WalletUI";
import ManualDeposit from "./ManualDeposit";

const CHIPS = [500, 1000, 2000, 5000, 10000, 20000];

export default function DepositFlow({ minDep, manualMax, bank, md, setMd, onManualSubmit, onUpiPay, upiBusy, copyText }) {
  const upiCfg = bank?.upi;
  // Frontend kill-switch: set to true to hide "Pay with UPI" everywhere without touching the backend.
  const UPI_PAUSED = false;
  const upiEnabled = !UPI_PAUSED && (!bank || !!upiCfg?.enabled);  // UPI is the default; hidden once config says disabled
  const [method, setMethod] = useState(upiEnabled ? "upi" : "bank");
  const [step, setStep] = useState(2);
  const [amount, setAmount] = useState("");
  const isManual = method === "bank";
  const max = isManual ? manualMax : (upiCfg?.max || manualMax);
  const n = Number(amount) || 0;
  const bonusPct = isManual ? (bank?.bonusPct ?? 5) : (upiCfg?.bonusPct ?? 0);
  const valid = n >= minDep && n <= max;

  const goContinue = () => { if (valid) onUpiPay(n); };
  const pickMethod = (m) => { if (m === "upi" && !upiEnabled) return; setMethod(m); setStep(2); };
  const tabCls = (active) => `rounded-xl py-2.5 text-sm font-bold transition-colors ${active ? "bg-[#f5b400] text-black ring-1 ring-[#f5b400]" : "bg-white/[0.04] text-white ring-1 ring-white/10 hover:ring-[#f5b400]/50"}`;

  return (
    <GlassCard testid="deposit-flow">
        <CardHeader title="Add Money" sub={isManual ? "Manual deposit · Bank / UPI ID / QR · verified by admin" : "UPI · instant credit after payment"} icon={ShieldCheck} />
        <div className="px-4 pt-3 grid grid-cols-2 gap-2" data-testid="deposit-method-tabs">
          <button type="button" onClick={() => pickMethod("upi")} disabled={!upiEnabled} aria-disabled={!upiEnabled} data-testid="deposit-method-upi"
            className={`relative ${upiEnabled ? tabCls(method === "upi") : "rounded-xl py-2.5 text-sm font-bold bg-white/[0.04] text-slate-400 ring-1 ring-white/10 cursor-not-allowed"}`}>
            <span className="inline-flex items-center gap-1.5"><Smartphone className="h-4 w-4" /> UPI</span>
            {upiEnabled
              ? <span className="absolute -top-2 right-2 rounded-full bg-emerald-500 px-2 py-[2px] text-[9px] font-extrabold uppercase tracking-wide text-black">Instant</span>
              : <span className="absolute -top-2 right-2 rounded-full bg-orange-500 px-2 py-[2px] text-[9px] font-extrabold uppercase tracking-wide text-black">Coming Soon</span>}
          </button>
          <button type="button" onClick={() => pickMethod("bank")} data-testid="deposit-method-bank" className={tabCls(method === "bank")}>
            Manual Deposit
          </button>
        </div>
        {isManual && (
          <ManualDeposit bank={bank} minDep={minDep} max={manualMax} bonusPct={bank?.bonusPct ?? 5} md={md} setMd={setMd} onSubmit={onManualSubmit} copyText={copyText} />
        )}
        {!isManual && <StepRail current={step} onStep={setStep} steps={[{ key: 2, label: "Amount", testid: "dep-step-2" }, { key: 3, label: "Pay via UPI app", testid: "dep-step-3", enabled: false }]} />}

        {!isManual && step === 2 && (
          <div className="relative px-4 pb-5 pt-4 space-y-4" data-testid="dep-amount-panel">

            <AmountChips chips={CHIPS.filter((c) => c <= max)} value={n} onPick={(c) => setAmount(String(c))} prefix="deposit-quick" tag={bonusPct > 0 ? `+${bonusPct}%` : null} />

            <div>
              <AmountField value={amount} onChange={(e) => setAmount(e.target.value)} min={minDep} max={max} valid={valid} testid="deposit-amount-input" />
              <div className="mt-2 flex items-center justify-between text-[11px] text-slate-500 px-1">
                <span>Min <span className="text-slate-300 font-semibold">{inr(minDep)}</span></span>
                <span>Max <span className="text-slate-300 font-semibold">{inr(max)}</span></span>
              </div>
              {bonusPct > 0 && n >= minDep && (
                <div className="mt-2 flex items-center justify-between rounded-xl bg-emerald-500/10 ring-1 ring-emerald-400/30 px-3 py-2 text-xs" data-testid="manual-bonus-preview">
                  <span className="text-emerald-300 font-semibold">You'll receive</span>
                  <span className="text-white font-bold tabular-nums">{inr(n + n * bonusPct / 100)} <span className="text-emerald-300/80 font-medium">({inr(n)} + {inr(n * bonusPct / 100)})</span></span>
                </div>
              )}
            </div>

            <Cta onClick={goContinue} disabled={!valid || upiBusy} testid="deposit-pay-btn">
              {upiBusy ? <><Loader2 className="h-5 w-5 animate-spin" /> Creating secure order…</>
                : <><Smartphone className="h-5 w-5" /> Pay with UPI {n >= minDep ? `· ${inr(n)}` : ""}</>}
            </Cta>
          </div>
        )}
    </GlassCard>
  );
}
